/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '@appblocks/codegeneration/Peripherals';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: CodeBlock[] = [];

    const project = feature.project;
    const NAME = feature.getSettingValue('NAME');
    const DUTY = feature.getSettingValue('DUTY');
    const FREQUENCY = feature.getSettingValue('FREQUENCY');
    const PIN = feature.getSettingValue('PIN');
    const device = PlatformUtils.getDeviceDefinition(feature.project.device.id);
    let PORT = feature.getSettingValue('PORT');
    if (PORT.indexOf('/') === 0) {
        PORT = `{${PORT}}`;
    }
    if (!PIN) {
        return blocks;
    }
    const dtsName = `${NAME.toLowerCase()}_pwm`;
    let pwmIndex = 0;

    // find pwm index;
    for (let i = 0; i < project.features.length; i++) {
        const tmpFeature = project.features[i];
        if (tmpFeature.id === 'pwm' || tmpFeature.id === 'servo') {
            if (tmpFeature.getID() === feature.getID()) {
                break;
            }
            pwmIndex++;
        }
    }

    const GPIO = Peripherals.getGPIOPeripheral(PIN, project.device.id);

    blocks.push({
        location: 'PRJ_CONF',
        id: 'CONFIG_PWM=y',
        blockType: BlockType.CODE,
        template: `
CONFIG_PWM=y`,
    });

    blocks.push({
        location: 'GLOBAL_INCLUDES',
        id: '#include <zephyr/drivers/pwm.h>',
        blockType: BlockType.CODE,
        template: `
#include <zephyr/drivers/pwm.h>`,
    });

    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
static const struct pwm_dt_spec ${dtsName} = PWM_DT_SPEC_GET(DT_NODELABEL(${dtsName}));
`,
    });

    blocks.push({
        location: 'APP_OVERLAY_DTS',
        id: 'PWM_LEDS',
        blockType: BlockType.CODE,
        template: `
pwmleds {
    compatible = "pwm-leds";
    /****PWM_LEDS****/
};`,
    });

    if (device.soc.indexOf('esp') === 0) {
        blocks.push({
            location: 'APP_OVERLAY',
            id: `${PORT}`,
            blockType: BlockType.CODE,
            template: `
&${PORT} {
    pinctrl-0 = <&${NAME}_default>;
    pinctrl-names = "default";
    status = "okay";
    #address-cells = <1>;
    #size-cells = <0>;
    /****${PORT}****/
};
`,
        });

        blocks.push({
            location: 'APP_OVERLAY_INCLUDES',
            id: '#include <zephyr/dt-bindings/pwm/pwm.h>',
            blockType: BlockType.CODE,
            template: `
#include <zephyr/dt-bindings/pwm/pwm.h>
`,
        });


        blocks.push({
            location: 'PWM_LEDS',
            blockType: BlockType.CODE,
            template: `
${dtsName}: ${dtsName} {
    label = "${NAME}";
    pwms = <&${PORT} ${pwmIndex} PWM_MSEC(20) PWM_POLARITY_NORMAL>;
};`,
        });


        blocks.push({
            location: 'APP_OVERLAY_PINCTRL',
            id: `${PORT}_default`,
            blockType: BlockType.CODE,
            template: `
${NAME}_default: ${NAME}_default {
    /****${NAME}_default****/
};
`,
        });

        if (PORT.indexOf('ledc') === 0) {
            blocks.push({
                location: `${PORT}`,
                blockType: BlockType.CODE,
                template: `
    channel${pwmIndex}@${pwmIndex} {
        reg = <0x${pwmIndex}>;
        timer = <${pwmIndex}>;
    };
    `,
            });

            blocks.push({
                location: `${NAME}_default`,
                blockType: BlockType.CODE,
                template: `
group${pwmIndex} {
    pinmux = <LEDC_CH${pwmIndex}_GPIO${GPIO.num}>;
    output-enable;
};
`,
            });
        } else {
            blocks.push({
                location: `${PORT}`,
                blockType: BlockType.CODE,
                template: `
    prescale = <255>;
    prescale-timer0 = <103>;
    prescale-timer1 = <0>;
    prescale-timer2 = <255>;
    `,
            });

            blocks.push({
                location: `${PORT}_default`,
                blockType: BlockType.CODE,
                template: `
group${pwmIndex} {
    pinmux = <${PORT.toUpperCase()}_OUT0A_GPIO${GPIO.num}>;
    output-enable;
};
`,
            });
        }
    }

    if (device.soc.indexOf('mim') >= 0) {
        const pin = device.pins.find((pin: any) => pin.num === PIN);
        const constantParts = pin.constant.split('_');
        constantParts.pop();
        constantParts.pop();
        const pinPrefix = constantParts.join('_');
        const pwm = pin.peripherals
            .find((peripheral: any) => peripheral.peripheralType === 'pwm');
        if (!pwm) {
            return [];
        }
        const pinmux = `${pinPrefix}_${pwm.peripheralPort}_${pwm.num}`;
        const channels: { [channel: string]: number } = {
            a: 0,
            b: 1,
            x: 2,
        };
        const channel = channels[pwm.num.match(/[abx]/)[0]];
        const pwmNodeLabel = `${pwm.peripheralPort}_${pwm.num.replace(/pwm[a-zA-Z]+(\d+)/g, 'pwm$1')}`;
        blocks.push({
            location: 'PWM_LEDS',
            blockType: BlockType.CODE,
            template: `
${dtsName}: ${dtsName} {
    label = "${NAME}";
    pwms = <&${pwmNodeLabel} ${channel} PWM_MSEC(20) PWM_POLARITY_NORMAL>;
};`,
        });

        blocks.push({
            location: 'APP_OVERLAY',
            blockType: BlockType.CODE,
            template: `
&${pwmNodeLabel} {
    status = "okay";
    pinctrl-0 = <&pinmux_${pwmNodeLabel}>;
    pinctrl-names = "default";
    nxp,prescaler = <64>;
};
`,
        });

        blocks.push({
            location: 'APP_OVERLAY_PINCTRL',
            id: `pinmux_${pwmNodeLabel}`,
            blockType: BlockType.CODE,
            template: `
pinmux_${pwmNodeLabel}: pinmux_${pwmNodeLabel} {
    /****pinmux_${pwmNodeLabel}****/
};
`,
        });

        blocks.push({
            location: `pinmux_${pwmNodeLabel}`,
            blockType: BlockType.CODE,
            template: `
group${pwmIndex} {
    pinmux = <&${pinmux}>;
    drive-strength = "r0-7";
    slew-rate = "fast";
    nxp,speed = "200-mhz";
};
`,
        });
    }

    blocks.push({
        location: `var_${DUTY}_set`,
        blockType: BlockType.CODE,
        template: `
uint32_t tmp_period = PWM_SEC(1.0 / ${FREQUENCY});
pwm_set_dt(&${dtsName}, tmp_period, ${DUTY} / 100.0 * tmp_period);
`,
    });

    blocks.push({
        location: `var_${FREQUENCY}_set`,
        blockType: BlockType.CODE,
        template: `
uint32_t tmp_period = PWM_SEC(1.0 / ${FREQUENCY});
pwm_set_dt(&${dtsName}, tmp_period, tmp_period * ${DUTY} / 100.0);
`,
    });

    return blocks;
};
